import { Button, buttonClasses, cn, EmptyState, PageHeader } from '@fruuit/ui'
import { Compass, Printer, SearchX } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router'
import { useSession } from '../../auth/session-context'
import { DOC_SECTIONS } from '../../features/admin-docs/content'
import { DeploymentSummary } from '../../features/admin-docs/DeploymentSummary'
import { DocSectionView } from '../../features/admin-docs/DocSectionView'
import { DocsMobileNav, DocsToc } from '../../features/admin-docs/DocsToc'
import { filterSections } from '../../features/admin-docs/search'
import { useTitle } from '../../lib/useTitle'
import '../../features/admin-docs/docs.css'

const POSITION = new Map(DOC_SECTIONS.map((section, index) => [section.id, index]))
const numberOf = (id: string) => POSITION.get(id) ?? 0

/** Scroll a section (or the top) into view after the shell's own route effects have run. */
function scrollToId(id: string, focusHeading: boolean) {
  requestAnimationFrame(() => {
    const target = document.getElementById(id)
    if (!target) return
    target.scrollIntoView?.({ block: 'start' })
    if (focusHeading) target.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true })
  })
}

/** Highlight the section being read (where IntersectionObserver exists). */
function useActiveSection(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(ids[0] ?? null)
  const key = ids.join(',')
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for `ids`
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const visible = new Set<string>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id)
          else visible.delete(entry.target.id)
        }
        const first = ids.find((id) => visible.has(id))
        if (first) setActive(first)
      },
      { rootMargin: '-15% 0px -70% 0px' },
    )
    for (const id of ids) {
      const element = document.getElementById(id)
      if (element) observer.observe(element)
    }
    return () => observer.disconnect()
  }, [key])
  return active
}

/** UI-043 in the shell: the same neutral copy as the full-page "not available" screen. */
function NotAvailable() {
  useTitle('Not available')
  return (
    <div className="flex flex-col items-center rounded-lg border border-dashed border-line-strong bg-surface/70 px-6 py-14 text-center">
      <span
        aria-hidden="true"
        className="mb-4 flex size-12 items-center justify-center rounded-full bg-wash text-teal-500 ring-1 ring-line [&>svg]:size-[22px]"
      >
        <Compass />
      </span>
      <h1 className="font-display text-[22px] font-semibold tracking-[var(--tracking-heading)] text-ink">
        This page is not available
      </h1>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-sage">
        The address may be mistyped, or the page may not be available to your account.
      </p>
      <Link to="/admin" className={cn('mt-5', buttonClasses({ variant: 'primary' }))}>
        Go to your workspace
      </Link>
    </div>
  )
}

/**
 * Admin → Dev docs (platform operators only): the technical handbook. How the system works
 * now, section by section, with "Demo mode" and "In production" callouts. Sections deep-link
 * as /admin/dev-docs#<id>. Business users have the Help centre at /help instead.
 */
export function DocsPage() {
  const me = useSession()
  if (me.platform_role !== 'platform_operator') return <NotAvailable />
  return <DevDocs />
}

function DevDocs() {
  useTitle('Dev docs')
  const { hash } = useLocation()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const matches = useMemo(() => filterSections(DOC_SECTIONS, query), [query])
  const visibleIds = useMemo(() => matches.map((match) => match.section.id), [matches])
  const activeId = useActiveSection(visibleIds)

  // Deep links: /admin/docs#identity on load and on back/forward.
  useEffect(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ''))
    if (id && POSITION.has(id)) scrollToId(id, true)
  }, [hash])

  const jumpTo = useCallback(
    (id: string) => {
      if (!visibleIds.includes(id)) setQuery('')
      navigate({ hash: id }, { replace: false })
      scrollToId(id, true)
    },
    [navigate, visibleIds],
  )

  const toTop = useCallback(() => {
    navigate({ hash: '' }, { replace: true })
    scrollToId('docs-top', false)
  }, [navigate])

  return (
    <div data-docs-root id="docs-top" className="scroll-mt-24">
      <PageHeader
        eyebrow="Platform operator · Dev docs"
        title="How Fruuit Engage works"
        description={
          <>
            The technical handbook for the platform operator: exactly how this deployment works
            today, area by area, with callouts for what is simplified in the demo and what changes
            in production. Business users should start with the{' '}
            <Link to="/help" className="font-medium text-teal underline underline-offset-2">
              Help centre
            </Link>
            .
          </>
        }
        actions={
          <Button
            variant="secondary"
            icon={<Printer />}
            onClick={() => window.print()}
            className="print:hidden"
          >
            Print
          </Button>
        }
      />

      <DeploymentSummary />

      <div className="docs-grid mt-8 grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-10">
        <aside className="hidden lg:block print:hidden">
          <div className="sticky top-6 max-h-[calc(100dvh-12rem)] overflow-y-auto pb-4">
            <DocsToc
              matches={matches}
              numberOf={numberOf}
              activeId={activeId}
              onJump={jumpTo}
              query={query}
              onQuery={setQuery}
              total={DOC_SECTIONS.length}
            />
          </div>
        </aside>

        <div className="flex min-w-0 flex-col gap-6">
          <div className="lg:hidden print:hidden">
            <DocsMobileNav
              matches={matches}
              numberOf={numberOf}
              onJump={jumpTo}
              query={query}
              onQuery={setQuery}
            />
          </div>

          {matches.length === 0 ? (
            <EmptyState
              icon={<SearchX />}
              title="No sections match"
              description={`Nothing in the handbook mentions “${query.trim()}”. Try a shorter word, a role or a setting name.`}
              action={
                <Button variant="secondary" onClick={() => setQuery('')}>
                  Show all sections
                </Button>
              }
            />
          ) : (
            matches.map(({ section }) => (
              <DocSectionView
                key={section.id}
                section={section}
                index={numberOf(section.id)}
                onTop={toTop}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

/** `/admin/dev-docs/:section` is an alias for `/admin/dev-docs#:section`. */
export function DocsSectionRedirect() {
  const { section = '' } = useParams()
  return <Navigate to={{ pathname: '/admin/dev-docs', hash: section }} replace />
}

/** `/admin/docs` and `/admin/docs/:section` (the old address) → `/admin/dev-docs#…`. */
export function LegacyDocsRedirect() {
  const { section } = useParams()
  const { hash } = useLocation()
  return (
    <Navigate
      to={{ pathname: '/admin/dev-docs', hash: section ?? hash.replace(/^#/, '') }}
      replace
    />
  )
}
